import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Award, ExternalLink } from "lucide-react";

export default function Credits() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/credits").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const artists = useMemo(() => {
    const set = new Set(items.map((c) => c.artist).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "All") return items;
    return items.filter((c) => c.artist === filter);
  }, [items, filter]);

  const visible = showAll || filter !== "All" ? filtered : filtered.slice(0, 10);

  if (!items.length) return null;

  return (
    <section id="credits" className="py-24 md:py-36 bg-charcoal text-warmwhite border-t border-warmwhite/10" data-testid="credits-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <p className="text-[11px] uppercase tracking-widest-custom text-gold mb-6">05 — Credits</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight max-w-3xl" data-testid="credits-title">
              Songwriting &amp; <span className="italic text-gold">production catalog.</span>
            </h2>
            <p className="mt-6 text-warmwhite/65 max-w-xl leading-relaxed">
              A growing list of recordings I&apos;ve had the privilege of co-writing or producing for artists I respect.
            </p>
          </div>
          <div className="flex items-center gap-3 text-warmwhite/55 text-sm">
            <Award className="w-5 h-5 text-gold" strokeWidth={1.2} />
            <span data-testid="credits-count">{items.length} credited releases</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="credits-filters">
          {artists.map((a) => (
            <button
              key={a}
              onClick={() => { setFilter(a); setShowAll(false); }}
              data-testid={`credits-filter-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-2 text-[11px] uppercase tracking-widest-custom border transition-colors duration-300 ${
                filter === a ? "border-gold bg-gold text-charcoal" : "border-warmwhite/30 text-warmwhite/70 hover:border-warmwhite"
              }`}
            >
              {a}
            </button>
          ))}
        </div>

        <div className="border-t border-warmwhite/15">
          {visible.map((c, i) => (
            <article
              key={c.id}
              data-testid={`credit-row-${c.id}`}
              className="group grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-9 border-b border-warmwhite/15 hover:bg-warmwhite/[0.03] transition-colors"
            >
              <div className="col-span-12 md:col-span-1 font-serif italic text-gold text-2xl leading-none">
                {String(i + 1).padStart(2, "0")}
              </div>

              <div className="col-span-12 md:col-span-4">
                {c.link ? (
                  <a
                    href={c.link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`credit-link-${c.id}`}
                    className="group/title inline-flex items-baseline gap-2"
                  >
                    <h3 className="font-serif text-2xl md:text-3xl font-light leading-tight group-hover/title:text-gold transition-colors" data-testid={`credit-title-${c.id}`}>
                      {c.title}
                    </h3>
                    <ExternalLink className="w-3.5 h-3.5 text-warmwhite/45 group-hover/title:text-gold transition-colors translate-y-0.5" />
                  </a>
                ) : (
                  <h3 className="font-serif text-2xl md:text-3xl font-light leading-tight" data-testid={`credit-title-${c.id}`}>
                    {c.title}
                  </h3>
                )}
                <p className="mt-2 text-sm text-warmwhite/70">{c.artist}</p>
                {c.role && (
                  <p className="mt-1 text-[10px] uppercase tracking-widest-custom text-warmwhite/45">{c.role}</p>
                )}
              </div>

              <div className="col-span-12 md:col-span-7" data-testid={`credit-people-${c.id}`}>
                <p className="text-[10px] uppercase tracking-widest-custom text-warmwhite/45 mb-3">Written with</p>
                <ul className="flex flex-wrap gap-x-3 gap-y-1.5 font-serif text-[15px] md:text-base text-warmwhite/85">
                  {c.credits.map((name, idx) => (
                    <li key={idx} className="flex items-center">
                      <span className={name === "James Robert Douglas" ? "text-gold italic" : ""}>{name}</span>
                      {idx < c.credits.length - 1 && <span className="ml-3 text-warmwhite/30">·</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        {filter === "All" && !showAll && items.length > 10 && (
          <div className="flex justify-center mt-12">
            <button
              onClick={() => setShowAll(true)}
              data-testid="credits-show-all"
              className="inline-flex items-center gap-2 border border-warmwhite/40 text-warmwhite px-8 py-4 text-[11px] uppercase tracking-widest-custom hover:bg-gold hover:text-charcoal hover:border-gold transition-colors duration-300"
            >
              Show all {items.length} credits
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
